import config from '@/config'

export interface BootstrapStep<T = unknown> {
  name: string
  critical?: boolean
  retries?: number
  run: () => Promise<T>
}

export interface BootstrapFailure {
  name: string
  error: unknown
  critical: boolean
}

export interface BootstrapResult {
  results: unknown[]
  failures: BootstrapFailure[]
  criticalFailures: BootstrapFailure[]
}

/**
 * The shape a rejected bootstrap step can carry: request.js rejects with a plain object,
 * everything else rejects with an Error.
 */
interface BootstrapErrorLike {
  statusCode?: number
  response?: { status?: number }
  error?: { message?: string }
  message?: string
  isTimeout?: boolean
}

/**
 * Reports a bootstrap failure to APM. Uses the same channel as request.js so all
 * login problems show up in one place.
 *
 * @param label short identifier, e.g. 'tenantSetting' or 'login'
 * @param error the rejected value
 */
export const captureBootstrapError = (label: string, error: unknown): void => {
  const reason = (error ?? {}) as BootstrapErrorLike
  const status = reason.statusCode ?? reason.response?.status ?? ''
  const message = reason.error?.message ?? reason.message ?? 'unknown error'

  window.elasticApm?.captureError?.(new Error(`login-bootstrap:${label}: ${status} ${message}`))
}

/**
 * Upper bound for a single login phase. A q deferred that never settles (e.g. a
 * cancelled axios request) bypasses config.requestTimeout, so we need our own ceiling.
 */
export const BOOTSTRAP_CEILING_MS = config.requestTimeout + 5000

export const withTimeout = <T>(promise: Promise<T> | T, ms: number, label: string): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(Object.assign(new Error(`${label} timed out after ${ms}ms`), { isTimeout: true }))
    }, ms)

    Promise.resolve(promise).then(
      value => {
        clearTimeout(timer)
        resolve(value)
      },
      error => {
        clearTimeout(timer)
        reject(error)
      },
    )
  })

const attempt = async(step: BootstrapStep, retriesLeft: number, failures: BootstrapFailure[]): Promise<unknown> => {
  try {
    return await step.run()
  } catch (error) {
    if (retriesLeft > 0) {
      return attempt(step, retriesLeft - 1, failures)
    }
    captureBootstrapError(step.name, error)
    failures.push({ name: step.name, error, critical: Boolean(step.critical) })
    return null
  }
}

/**
 * Runs all bootstrap steps in parallel and never rejects. Each step reports its own
 * failure, so a single broken request can no longer prevent the login from finishing.
 */
export const runBootstrapSteps = async(steps: BootstrapStep[]): Promise<BootstrapResult> => {
  const failures: BootstrapFailure[] = []
  const results = await Promise.all(steps.map(step => attempt(step, step.retries || 0, failures)))

  return {
    results,
    failures,
    criticalFailures: failures.filter(failure => failure.critical),
  }
}
